import { AuthTokens, User } from '../domain/types';

const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_KEY = 'user';
const JWT_PARTS_COUNT = 3;
const CLOCK_SKEW_SECONDS = 5;

export class AuthStorage {
  private parseJwtPayload(token: string): Record<string, unknown> | null {
    const normalized = token.trim();
    if (!normalized) {
      return null;
    }

    const parts = normalized.split('.');
    if (parts.length !== JWT_PARTS_COUNT) {
      return null;
    }

    try {
      const payload = parts[1];
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
      const decoded = atob(padded);
      return JSON.parse(decoded);
    } catch {
      return null;
    }
  }

  private isTokenExpired(payload: Record<string, unknown>): boolean {
    const exp = payload.exp;
    if (typeof exp !== 'number') {
      return false;
    }
    const now = Math.floor(Date.now() / 1000);
    return exp <= now + CLOCK_SKEW_SECONDS;
  }

  getAccessToken(): string | null {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (!token) {
      return null;
    }

    const payload = this.parseJwtPayload(token);
    if (!payload || this.isTokenExpired(payload)) {
      this.clearAuthData();
      return null;
    }

    return token;
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  getUser(): User | null {
    const userStr = localStorage.getItem(USER_KEY);
    if (!userStr) return null;
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }

  setAuthData(tokens: AuthTokens, user: User): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, tokens.access);
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }

  setTokens(tokens: AuthTokens): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, tokens.access);
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh);
  }

  clearAuthData(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }

  hasAccessToken(): boolean {
    return !!this.getAccessToken();
  }
}
